import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import {
  applyPromotionDiscount,
  computeGrossPrice,
  formatTaxHint,
  getActivePromotion,
  getProductCategoryLabels,
  getPriceRange,
  getPromotionHintText,
  getVariantBySelection,
  getVariantStock,
  isVariantInStock,
  resolveProductImageUrl,
  toCurrency,
} from "./shopUtils";

const parseVariantKey = (variantKey) => {
  const parsed = {};
  String(variantKey || "")
    .split("|")
    .forEach((part) => {
      const separatorIndex = part.indexOf(":");
      if (separatorIndex < 1) return;
      const name = part.slice(0, separatorIndex);
      const value = part.slice(separatorIndex + 1);
      if (!name || !value) return;
      parsed[name] = value;
    });
  return parsed;
};

const buildSelectedValues = (product, variantKey = null) => {
  const defaults = {};

  (product?.optionSets || []).forEach((set) => {
    defaults[set.name] = set.values?.[0] || "";
  });

  if (!variantKey) return defaults;

  const parsed = parseVariantKey(variantKey);
  Object.entries(parsed).forEach(([key, value]) => {
    if (Object.prototype.hasOwnProperty.call(defaults, key)) {
      defaults[key] = value;
    }
  });

  return defaults;
};

export default function ShopProductDetailPage({
  product,
  loadingProduct,
  initialVariantKey,
  onVariantChange,
  onAddToCart,
  onGoToCart,
  onBack,
  hasContractContext,
  checkoutDisabledMessage,
  checkoutContextNote,
  isMemberCatalogView,
}) {
  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedValues, setSelectedValues] = useState({});
  const [cartToastOpen, setCartToastOpen] = useState(false);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const lastReportedVariantKey = useRef(null);
  const lastInitializedProductId = useRef(null);
  const suppressVariantChangeRef = useRef(false);

  const hasMatchingInitialVariant = useMemo(() => {
    if (!initialVariantKey) return false;
    return (product?.variants || []).some(
      (variant) =>
        variant?.isActive !== false &&
        String(variant?.key || "") === String(initialVariantKey)
    );
  }, [initialVariantKey, product?.variants]);

  useEffect(() => {
    const productId = String(product?._id || "");
    if (!productId) {
      lastInitializedProductId.current = null;
      suppressVariantChangeRef.current = false;
      return;
    }
    if (lastInitializedProductId.current === productId) return;

    lastInitializedProductId.current = productId;
    suppressVariantChangeRef.current = hasMatchingInitialVariant;
    setQuantity(1);
    setActiveImageIndex(0);
    lastReportedVariantKey.current = null;
    setSelectedValues(buildSelectedValues(product, initialVariantKey));
  }, [hasMatchingInitialVariant, initialVariantKey, product]);

  useEffect(() => {
    if (!product || !initialVariantKey) return;
    suppressVariantChangeRef.current = hasMatchingInitialVariant;
    const parsed = parseVariantKey(initialVariantKey);
    setSelectedValues((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(parsed).forEach(([key, value]) => {
        if (next[key] !== value) {
          next[key] = value;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [hasMatchingInitialVariant, product, initialVariantKey]);

  const selectedVariant = useMemo(
    () => getVariantBySelection(product, selectedValues),
    [product, selectedValues]
  );
  const selectedVariantStock = getVariantStock(selectedVariant);
  const selectedVariantInStock = isVariantInStock(selectedVariant);
  const maxSelectableQuantity = Number.isInteger(selectedVariantStock)
    ? selectedVariantStock
    : null;
  const referenceDate = useMemo(() => new Date(nowTs), [nowTs]);
  const activePromotion = useMemo(
    () => getActivePromotion(product, referenceDate),
    [product, referenceDate]
  );
  const hasActivePromotion = Boolean(activePromotion);
  const promotionHintText = useMemo(
    () => getPromotionHintText(activePromotion, referenceDate),
    [activePromotion, referenceDate]
  );

  useEffect(() => {
    setNowTs(Date.now());
  }, [product?._id]);

  useEffect(() => {
    if (!hasActivePromotion) return undefined;
    const intervalId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [hasActivePromotion]);

  useEffect(() => {
    if (!selectedVariant?.key || !onVariantChange) return;
    if (suppressVariantChangeRef.current) {
      if (selectedVariant.key === initialVariantKey) {
        lastReportedVariantKey.current = selectedVariant.key;
        suppressVariantChangeRef.current = false;
      }
      return;
    }
    if (selectedVariant.key === lastReportedVariantKey.current) return;
    lastReportedVariantKey.current = selectedVariant.key;
    onVariantChange(selectedVariant.key);
  }, [initialVariantKey, onVariantChange, selectedVariant]);

  useEffect(() => {
    if (!product || !selectedVariant?.key) return;

    if (!selectedVariantInStock) {
      setQuantity(1);
      return;
    }

    if (maxSelectableQuantity !== null) {
      setQuantity((prev) => {
        const normalizedPrev = Math.max(1, Number(prev) || 1);
        return normalizedPrev > maxSelectableQuantity
          ? Math.max(maxSelectableQuantity, 1)
          : normalizedPrev;
      });
    }
  }, [
    maxSelectableQuantity,
    product,
    selectedVariant?.key,
    selectedVariantInStock,
  ]);

  if (loadingProduct) {
    return (
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Skeleton
              variant="rectangular"
              height={360}
              sx={{ borderRadius: 2 }}
            />
            <Stack spacing={0.9} sx={{ mt: 1.5 }}>
              <Skeleton width="72%" height={32} />
              <Skeleton width="38%" height={26} />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={1.5}>
              <Skeleton width="100%" height={72} />
              <Skeleton width="52%" height={28} />
              <Skeleton width="100%" height={58} />
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  if (!product) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, textAlign: "center" }}
      >
        <Stack spacing={1.5} alignItems="center">
          <Iconify
            icon="eva:alert-circle-outline"
            width={34}
            sx={{ color: "warning.main" }}
          />
          <Typography variant="h6">Produkt nicht gefunden</Typography>
          <Typography variant="body2" color="text.secondary">
            Das angeforderte Produkt ist nicht mehr verfuegbar oder wurde
            entfernt.
          </Typography>
          <Button variant="contained" onClick={onBack}>
            Zurueck zum Katalog
          </Button>
        </Stack>
      </Paper>
    );
  }

  const images = Array.isArray(product.images) ? product.images : [];
  const productCategories = getProductCategoryLabels(product);
  const activeImage = images[activeImageIndex] || images[0] || "";
  const productTaxRate = Number(product.taxRate || 0);
  const taxHint = formatTaxHint(productTaxRate);
  const netBaseUnitPrice = Number(
    selectedVariant?.price ?? product.basePrice ?? 0
  );
  const baseUnitPrice = computeGrossPrice(netBaseUnitPrice, productTaxRate);
  const unitPrice = computeGrossPrice(
    applyPromotionDiscount(netBaseUnitPrice, activePromotion),
    productTaxRate
  );
  const safeQuantity = selectedVariantInStock
    ? Math.max(
        1,
        maxSelectableQuantity === null
          ? Number(quantity || 1)
          : Math.min(Number(quantity || 1), Math.max(maxSelectableQuantity, 1))
      )
    : 1;
  const lineTotal = unitPrice * safeQuantity;
  const hasVariantOptions = (product.optionSets || []).length > 0;
  const canShowStockCount =
    !isMemberCatalogView || Boolean(product?.showVariantStockToMembers);
  const originalRange = getPriceRange(product);
  const discountedRange = activePromotion
    ? getPriceRange(product, { promotion: activePromotion, referenceDate })
    : originalRange;
  const displayPrice =
    hasVariantOptions && discountedRange.min !== discountedRange.max
      ? `${toCurrency(discountedRange.min)} - ${toCurrency(
          discountedRange.max
        )}`
      : toCurrency(unitPrice);
  const originalDisplayPrice =
    hasVariantOptions && originalRange.min !== originalRange.max
      ? `${toCurrency(originalRange.min)} - ${toCurrency(originalRange.max)}`
      : toCurrency(baseUnitPrice);

  const handleAddToCart = () => {
    if (
      !selectedVariant ||
      !onAddToCart ||
      !hasContractContext ||
      !selectedVariantInStock
    )
      return;
    onAddToCart({
      productId: product._id,
      productName: product.name,
      variantKey: selectedVariant.key,
      variantLabel: selectedVariant.label,
      variantOptions: selectedVariant.optionValues || [],
      unitPrice,
      taxRate: productTaxRate,
      quantity: safeQuantity,
      image: images[0] || "",
      availableStock: selectedVariantStock,
      isInStock: selectedVariantInStock,
    });
    setCartToastOpen(true);
  };

  return (
    <Stack spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 15 } }}>
      {checkoutContextNote ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {checkoutContextNote}
        </Alert>
      ) : null}

      {!hasContractContext && checkoutDisabledMessage ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {checkoutDisabledMessage}
        </Alert>
      ) : null}

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 3, overflow: "hidden", p: 1.5 }}
          >
            <Box sx={{ position: "relative" }}>
              {activeImage ? (
                <Box
                  component="img"
                  src={resolveProductImageUrl(activeImage)}
                  alt={product.name}
                  sx={{
                    width: "100%",
                    height: { xs: 300, sm: 380, md: 440 },
                    objectFit: "cover",
                    borderRadius: 2,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: { xs: 300, sm: 380, md: 440 },
                    borderRadius: 2,
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Iconify
                    icon="eva:shopping-bag-fill"
                    width={70}
                    sx={{ color: "grey.300" }}
                  />
                </Box>
              )}

              <ButtonBase
                onClick={onBack}
                sx={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.36)",
                  bgcolor: "rgba(17,24,39,0.58)",
                  color: "common.white",
                  px: 1.2,
                  py: 0.65,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  backdropFilter: "blur(3px)",
                }}
              >
                <Iconify icon="eva:arrow-ios-back-fill" width={15} />
                <Typography
                  variant="caption"
                  component="span"
                  sx={{ fontWeight: 700, lineHeight: 1.1 }}
                >
                  Zur Produktliste
                </Typography>
              </ButtonBase>

              {activePromotion ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    borderRadius: 999,
                    px: 1.15,
                    py: 0.5,
                    color: "common.white",
                    fontWeight: 800,
                    fontSize: "0.8rem",
                    letterSpacing: "0.02em",
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #f97316 100%)",
                    boxShadow: "0 8px 20px rgba(239, 68, 68, 0.35)",
                  }}
                >
                  -
                  {Number(activePromotion.discountPercent || 0).toLocaleString(
                    "de-DE"
                  )}
                  %
                </Box>
              ) : null}
            </Box>

            {images.length > 1 ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1.5, overflowX: "auto", pb: 0.5 }}
              >
                {images.map((image, index) => (
                  <ButtonBase
                    key={`${image}-${index}`}
                    onClick={() => setActiveImageIndex(index)}
                    sx={{
                      borderRadius: 1.5,
                      border: "2px solid",
                      borderColor:
                        index === activeImageIndex ? "primary.main" : "divider",
                      flex: "0 0 auto",
                    }}
                  >
                    <Box
                      component="img"
                      src={resolveProductImageUrl(image)}
                      alt={`${product.name} ${index + 1}`}
                      sx={{
                        width: 72,
                        height: 72,
                        objectFit: "cover",
                        borderRadius: 1,
                      }}
                    />
                  </ButtonBase>
                ))}
              </Stack>
            ) : null}

            <Stack spacing={0.35} sx={{ mt: 1.5, px: 0.25 }}>
              {productCategories.length ? (
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 0.35 }}
                >
                  {productCategories.map((category) => (
                    <Chip
                      key={`${product._id}-${category}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={category}
                      sx={{ alignSelf: "flex-start" }}
                    />
                  ))}
                </Stack>
              ) : null}
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: "1.25rem", md: "1.55rem" },
                  fontWeight: 700,
                }}
              >
                {product.name}
              </Typography>
              {activePromotion ? (
                <Stack spacing={0.1}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      textDecoration: "line-through",
                      textDecorationThickness: "2px",
                      fontWeight: 600,
                    }}
                  >
                    {originalDisplayPrice}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.3rem" },
                      fontWeight: 800,
                      color: "error.main",
                    }}
                  >
                    {displayPrice}
                  </Typography>
                </Stack>
              ) : (
                <Typography
                  variant="h6"
                  color="primary.main"
                  sx={{
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                    fontWeight: 800,
                  }}
                >
                  {displayPrice}
                </Typography>
              )}
              {taxHint ? (
                <Typography variant="caption" color="text.secondary">
                  {taxHint}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 3, p: { xs: 2, md: 2.5 } }}
          >
            <Stack spacing={2.25}>
              {activePromotion ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.35,
                    borderRadius: 1.8,
                    borderColor: "rgba(239,68,68,0.35)",
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(249,115,22,0.06) 100%)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "error.dark",
                      fontWeight: 700,
                      lineHeight: 1.4,
                    }}
                  >
                    {promotionHintText}
                  </Typography>
                </Paper>
              ) : null}

              {product.description ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ lineHeight: 1.75 }}
                >
                  {product.description}
                </Typography>
              ) : null}

              {!selectedVariantInStock ? (
                <Alert severity="warning" sx={{ borderRadius: 1.8 }}>
                  Diese Variante ist aktuell nicht auf Lager.
                </Alert>
              ) : null}

              {selectedVariantInStock && canShowStockCount ? (
                <Paper
                  variant="outlined"
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderRadius: 1.8,
                    bgcolor: "grey.50",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Bestand:{" "}
                    {maxSelectableQuantity === null
                      ? "unbegrenzt"
                      : `${maxSelectableQuantity} verfuegbar`}
                  </Typography>
                </Paper>
              ) : null}

              {(product.optionSets || []).map((set) => {
                const hasColorPreview = Boolean(set.isColorSet && set.colorMap);
                return (
                  <Box key={set.name}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {set.name}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {(set.values || []).map((value) => {
                        const isSelected = selectedValues[set.name] === value;
                        const hex = hasColorPreview
                          ? set.colorMap?.[value]
                          : null;
                        return (
                          <ButtonBase
                            key={value}
                            onClick={() =>
                              setSelectedValues((prev) => ({
                                ...prev,
                                [set.name]: value,
                              }))
                            }
                            sx={{
                              borderRadius: 2,
                              border: "2px solid",
                              borderColor: isSelected
                                ? "primary.main"
                                : "divider",
                              px: hex ? 1 : 1.75,
                              py: 0.8,
                              fontWeight: isSelected ? 700 : 500,
                              bgcolor: isSelected
                                ? "primary.lighter"
                                : "background.paper",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.8,
                            }}
                          >
                            {hex ? (
                              <Box
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: "50%",
                                  bgcolor: hex,
                                  border: "1px solid rgba(0,0,0,0.2)",
                                }}
                              />
                            ) : null}
                            <Typography
                              variant="body2"
                              component="span"
                              fontWeight={isSelected ? 700 : 500}
                            >
                              {value}
                            </Typography>
                          </ButtonBase>
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Menge
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <IconButton
                    onClick={() =>
                      setQuantity((prev) => Math.max(1, Number(prev) - 1))
                    }
                    disabled={safeQuantity <= 1 || !selectedVariantInStock}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                  >
                    <Iconify icon="eva:minus-fill" width={16} />
                  </IconButton>
                  <Typography
                    variant="h6"
                    sx={{ minWidth: 40, textAlign: "center" }}
                  >
                    {safeQuantity}
                  </Typography>
                  <IconButton
                    onClick={() => setQuantity((prev) => Number(prev) + 1)}
                    disabled={
                      !selectedVariantInStock ||
                      (maxSelectableQuantity !== null &&
                        safeQuantity >= maxSelectableQuantity)
                    }
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                    }}
                  >
                    <Iconify icon="eva:plus-fill" width={16} />
                  </IconButton>
                </Stack>
                {selectedVariantInStock && maxSelectableQuantity !== null ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    Maximal {maxSelectableQuantity} Stueck verfuegbar.
                  </Typography>
                ) : null}
              </Box>

              <Paper
                variant="outlined"
                sx={{ p: 1.75, borderRadius: 2, bgcolor: "grey.50" }}
              >
                <Stack spacing={0.75}>
                  {selectedVariant?.label &&
                  selectedVariant.label !== "Standard" ? (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" color="text.secondary">
                        Variante
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedVariant.label}
                      </Typography>
                    </Stack>
                  ) : null}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" color="text.secondary">
                      Einzelpreis
                    </Typography>
                    {activePromotion ? (
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            textDecoration: "line-through",
                            textDecorationThickness: "2px",
                          }}
                        >
                          {toCurrency(baseUnitPrice)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "error.main", fontWeight: 700 }}
                        >
                          {toCurrency(unitPrice)}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2">
                        {toCurrency(unitPrice)}
                      </Typography>
                    )}
                  </Stack>
                  {taxHint ? (
                    <Typography variant="caption" color="text.secondary">
                      {taxHint}
                    </Typography>
                  ) : null}
                  <Divider />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      Gesamt
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      color="primary.main"
                    >
                      {toCurrency(lineTotal)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<Iconify icon="eva:shopping-cart-fill" width={20} />}
                disabled={
                  !selectedVariant ||
                  !hasContractContext ||
                  !selectedVariantInStock
                }
                onClick={handleAddToCart}
                sx={{ py: 1.25, borderRadius: 2, fontWeight: 700 }}
              >
                In den Warenkorb
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Iconify icon="eva:arrow-forward-fill" width={18} />}
                onClick={onGoToCart}
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  borderRadius: 2,
                  fontWeight: 700,
                }}
              >
                Zum Warenkorb
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={cartToastOpen}
        autoHideDuration={2500}
        onClose={() => setCartToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify
              icon="eva:checkmark-circle-2-fill"
              width={20}
              sx={{ color: "#4ade80" }}
            />
            <Typography variant="body2">
              &bdquo;{product.name}&ldquo; wurde in den Warenkorb gelegt.
            </Typography>
          </Stack>
        }
        ContentProps={{
          sx: { borderRadius: 2, px: 2.25, py: 1.25 },
        }}
      />
    </Stack>
  );
}
